import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { PaymentBalance, PaymentMethodId } from '@/types/payment'
import type { RootState } from '@/store/store'
import type { CreateNowPaymentData, DepositStep } from '@/types/payment'
import { paymentApi } from '@/store/api/paymentApi'

interface PaymentState {
	balances: PaymentBalance[]
	gridSelectedCurrencyCodes: string[]
	isWalletConnectOpen: boolean
	depositStep: DepositStep
	depositCurrencyCode: string | null
	depositMethodId: PaymentMethodId | null
	depositAmount: string
	depositNowPaymentData: CreateNowPaymentData | null
	depositMetamaskAddress: string | null
}

const initialState: PaymentState = {
	balances: [],
	gridSelectedCurrencyCodes: [],
	isWalletConnectOpen: true,
	depositStep: 'method',
	depositCurrencyCode: null,
	depositMethodId: null,
	depositAmount: '',
	depositNowPaymentData: null,
	depositMetamaskAddress: null,
}

const paymentSlice = createSlice({
	name: 'payment',
	initialState,
	reducers: {
		setBalances(state, action: PayloadAction<PaymentBalance[]>) {
			state.balances = action.payload
		},
		applyBalanceUpdate(state, action: PayloadAction<{ currency_code: string; balance: number }>) {
			const code = action.payload.currency_code.toUpperCase()
			const row = state.balances.find((balance) => balance.currency_code.toUpperCase() === code)
			if (!row) {
				return
			}
			row.balance = String(action.payload.balance)
		},
		setGridCurrencyChecked(state, action: PayloadAction<{ code: string; checked: boolean }>) {
			const normalizedCode = action.payload.code.toUpperCase()
			const existingIndex = state.gridSelectedCurrencyCodes.findIndex((code) => code.toUpperCase() === normalizedCode)

			if (action.payload.checked) {
				if (existingIndex === -1) {
					state.gridSelectedCurrencyCodes.push(action.payload.code)
				}

				return
			}

			if (existingIndex !== -1) {
				state.gridSelectedCurrencyCodes.splice(existingIndex, 1)
			}
		},
		clearGridSelectedCurrencyCodes(state) {
			state.gridSelectedCurrencyCodes = []
		},
		setWalletConnectOpen(state, action: PayloadAction<boolean>) {
			state.isWalletConnectOpen = action.payload
		},
		setDepositStep(state, action: PayloadAction<DepositStep>) {
			state.depositStep = action.payload
		},
		setDepositCurrencyCode(state, action: PayloadAction<string | null>) {
			state.depositCurrencyCode = action.payload
		},
		setDepositMethodId(state, action: PayloadAction<PaymentMethodId | null>) {
			state.depositMethodId = action.payload
		},
		setDepositAmount(state, action: PayloadAction<string>) {
			state.depositAmount = action.payload
		},
		setDepositNowPaymentData(state, action: PayloadAction<CreateNowPaymentData | null>) {
			state.depositNowPaymentData = action.payload
		},
		setDepositMetamaskAddress(state, action: PayloadAction<string | null>) {
			state.depositMetamaskAddress = action.payload
		},
		resetDepositFlow(state) {
			state.depositStep = 'method'
			state.depositCurrencyCode = null
			state.depositMethodId = null
			state.depositAmount = ''
			state.depositNowPaymentData = null
			state.depositMetamaskAddress = null
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(paymentApi.endpoints.getBalances.matchFulfilled, (state, action) => {
			state.balances = Array.isArray(action.payload.data) ? action.payload.data : []
		})
	},
})

export const {
	setBalances,
	applyBalanceUpdate,
	setGridCurrencyChecked,
	clearGridSelectedCurrencyCodes,
	setWalletConnectOpen,
	setDepositStep,
	setDepositCurrencyCode,
	setDepositMethodId,
	setDepositAmount,
	setDepositNowPaymentData,
	setDepositMetamaskAddress,
	resetDepositFlow,
} = paymentSlice.actions

export const selectBalances = (state: RootState) => state.payment.balances
export const selectDefaultBalance = (state: RootState) => state.payment.balances.find((balance) => balance.is_default) ?? state.payment.balances[0] ?? null
export const selectGridSelectedCurrencyCodes = (state: RootState) => state.payment.gridSelectedCurrencyCodes
export const selectIsWalletConnectOpen = (state: RootState) => state.payment.isWalletConnectOpen
export const selectDepositStep = (state: RootState) => state.payment.depositStep
export const selectDepositCurrencyCode = (state: RootState) => state.payment.depositCurrencyCode
export const selectDepositMethodId = (state: RootState) => state.payment.depositMethodId
export const selectDepositAmount = (state: RootState) => state.payment.depositAmount
export const selectDepositNowPaymentData = (state: RootState) => state.payment.depositNowPaymentData
export const selectDepositMetamaskAddress = (state: RootState) => state.payment.depositMetamaskAddress

export default paymentSlice.reducer
